import { user } from 'ampersand-app'
import request from '@/modules/base/util/request'
import config from '@/config'
import { SORT_ORDER } from '@/enums/pagination'
import download from 'downloadjs'
import SparkMD5 from 'spark-md5'

import moment from 'moment'
import { errorCodes, subscriptionLimits } from '@/modules/base/util/constants'
import { setCollaboratorsData, updateFolders } from '@/redux/ducks/document/list'

import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'
import getCollaborationRequestHeader from '@/modules/document/_helpers/getCollaborationRequestHeader'
import { getList, LIST_TYPES } from '@/redux/ducks/lists'
import { TenantCollaboratorsService } from '@/modules/tenant/tenantCollaboratorsService'
import axios from 'axios'
import { fetchFolders, fetchListAction } from '@/redux/ducks/lists/requests/documentList'
import customEncodeUri from '@/utils/customEncodeUri'
import { addPending, removeChecksumCalculation, updateChecksumCalculation, updatePending } from '@/redux/ducks/document/upload'
// FLAG:useDocuAi — upload hardening: retry policy + the source predicate that gates it. The
// predicate covers the receipt sources always and 'documents' when the flag is on; with the flag off
// it collapses to isReceiptUploadSource, the exact test these call sites used before.
import { isHardenedUploadSource } from '@/redux/ducks/document/_helpers/uploadHardening'
import { withUploadRetry } from '@/redux/ducks/document/_helpers/retryUpload'
import { runReceiptSingleUpload as runReceiptSingleUploadChain } from '@/redux/ducks/document/_helpers/runReceiptSingleUpload'
import { runDocumentSingleUpload as runDocumentSingleUploadChain } from '@/redux/ducks/document/_helpers/runDocumentSingleUpload'
import { enrichUploadError } from '@/redux/ducks/document/_helpers/uploadErrorMessage'
import { isReceiptUploadSource } from '@/redux/ducks/document/_helpers/uploadAdmission'
import { dispatch, getAppState } from '@/redux/store'
import { UNSAFE_openUpgradeModal as openUpgradeModal } from '@/hooks/useUpgradeModal'
import { askDuplicateUploadResolution } from '@/modules/file/_helpers/duplicateUploadPrompt'
import documentConfig from '@/modules/document/config'
import { pick } from 'lodash'
import { withDmsFrontendToken } from '@/modules/file/_helpers/dms-authenticated-request'

export const CHUNK_SIZE = 25 * 1024 * 1024
export const SINGLE_UPLOAD_MAX_SIZE = 95 * 1024 * 1024
const { DOCUMENTS_LIST_TYPE } = LIST_TYPES

// FLAG:useDocuAi — request timeouts for the hardened upload path. Every upload call used the
// axios default (no timeout), so a hung connection held its concurrency slot indefinitely and the
// batch backstop was the only thing that ever noticed. Applied via `timeoutFor()` below, which
// returns undefined for legacy sources so their requests keep the previous no-timeout behaviour.
//
// The timeout is what makes the retry policy reachable at all for a STALLED connection: without one
// the promise never settles, so there is nothing for withUploadRetry to catch.
const PREPARE_TIMEOUT_MS = 30000
const UPLOAD_TIMEOUT_MS = 300000
// complete-upload is the expensive one: it fetches the whole file back from the DMS and writes it
// as a Postgres large object inside the request transaction, so it needs far more headroom than a
// normal API call.
const COMPLETE_TIMEOUT_MS = 120000

const timeoutFor = (source, ms) => (isHardenedUploadSource(source) ? { timeout: ms } : {})

const completeUpload = ({ checksum, name, uploadToken, analyze, minioId, source }) => axios.post(`${config.resourceHost}dms-upload/complete-upload`, {
  checksum,
  name,
  uploadToken,
  analyze,
  minioId,
}, {
  headers: {
    ...getCollaborationRequestHeader(),
    Authorization: `Bearer ${user.token}`,
  },
  ...timeoutFor(source, COMPLETE_TIMEOUT_MS),
})

const onUploadError = ({ response }, { uploadToken, uuid }) => {
  const notAllowedError = response?.data?.meta?.upload.find(({ code }) => code === errorCodes.NOT_ALLOWED)
  const message = response?.data?.message ?? response?.message ?? lang.documents.upload.errors.uploadFailed
  const meta = response?.data?.meta ?? {}
  const hasNotAllowedCode = (value) => Array.isArray(value) && value.some((item) => item?.code === errorCodes.NOT_ALLOWED)
  const hasMimetypeNotAllowed = hasNotAllowedCode(meta?.mimetype)
  const hasExtensionNotAllowed = Object.entries(meta).some(([key, value]) => key.toLowerCase().includes('extension') && hasNotAllowedCode(value))
  const hasExtensionNotAllowedMessage = message.toLowerCase().includes('extension') && message.toLowerCase().includes('not allowed')
  const isFileTypeNotAllowed = hasMimetypeNotAllowed || hasExtensionNotAllowed || hasExtensionNotAllowedMessage
  const translatedError = response?.data?.meta?.mimetype?.some(mt => mt.code === errorCodes.NOT_ALLOWED) ? lang.documents.calculation.errors.mimetype : undefined
  if (notAllowedError) {
    toast.showToast({ type: 'error', message: lang.documents.folder.notAllowed })
  }
  if (uploadToken) dispatch(updatePending({ error: message, uploadToken }))
  else if (uuid) dispatch(updateChecksumCalculation({ error: message, uuid, translatedError }))

  if (isFileTypeNotAllowed) {
    toast.showToast({ type: 'info', message: lang.documents.errors.allFilesNotAllowed(1) })
  }

  if (response?.data?.meta?.[subscriptionLimits.STORAGE_SPACE]) {
    openUpgradeModal({
      subscriptionChange: true,
      subscriptionType: 'document',
      limitReached: subscriptionLimits.STORAGE_SPACE,
    })
  } else if (response?.data?.meta?.[subscriptionLimits.DOCUMENT_COUNT] || response?.data?.meta?.[subscriptionLimits.DOCUMENT_COUNT_YEAR]) {
    openUpgradeModal({
      subscriptionChange: true,
      subscriptionType: 'document',
      limitReached: subscriptionLimits.DOCUMENT_COUNT,
    })
  }
}

export const calculateChecksum = async(uuid, file, totalChunks) => {
  const spark = new SparkMD5.ArrayBuffer()
  let currentChunk = 0

  const fileReader = new FileReader()

  dispatch(updateChecksumCalculation({ uuid, running: true }))
  return new Promise((resolve, reject) => {
    fileReader.onload = (e) => {
      spark.append(e.target.result)
      currentChunk++

      dispatch(updateChecksumCalculation({ uuid, currentChunk }))
      if (currentChunk < totalChunks) {
        setTimeout(loadNextChunk, 10)
      } else {
        const checksum = spark.end()
        resolve({ checksum, totalChunks })
      }
    }

    fileReader.onerror = () => {
      dispatch(updateChecksumCalculation({ uuid, error: lang.documents.uploadParseError }))
      reject(new Error('FileReader error'))
    }

    const loadNextChunk = () => {
      const start = currentChunk * CHUNK_SIZE
      const end = Math.min(start + CHUNK_SIZE, file.size)
      const chunk = file.slice(start, end)
      fileReader.readAsArrayBuffer(chunk)
    }

    loadNextChunk()
  })
}

const { resourceHost } = config

export const fileMetaKeys = [
  'recordDate',
  'entryDate',
  'receiptNumber',
  'supplier',
  'customer',
  'name',
  'tags',
  'notes',
  'metaData',
  'isManuallyPaid',
  'documentInvoiceDetails',
]

export const buildDateFilter = date => {
  return date
    .map(({ fromDate, toDate }) => ({ dateFrom: fromDate, dateTo: toDate, type: 'inRange', filterType: 'date' }))
    .reduce((result, current, index) => {
      if (index === 0) {
        return { recordDate: current }
      } else {
        if (index === 1) {
          return {
            recordDate: {
              operator: 'OR',
              filterType: 'date',
              condition1: result.recordDate,
              condition2: current,
              conditions: [
                result.recordDate,
                current,
              ],
            },
          }
        } else {
          result.recordDate[`condition${index + 1}`] = current
          result.recordDate.conditions.push(current)
          return result
        }
      }
    }, {})
}

const dataToSend = (pagination, options) => {
  const { offset = 0, limit = 20, orderBy = null, sortOrder = SORT_ORDER.DESC, path } = pagination || {}
  const { includes, excludes, searchTerm, filter, tagFilter, favoritesFirst } = options || {}

  const requestData = {
    offset,
    limit,
    // the /file endpoints validate includes/excludes as comma-separated STRINGS
    // (paginateAllTheThings splits on ','); an array in axios params serializes to
    // includes[]=… and is rejected with 400 NO_STRING
    excludes: Array.isArray(excludes) ? (excludes.length ? excludes.join(',') : undefined) : excludes,
    includes: Array.isArray(includes) ? (includes.length ? includes.join(',') : undefined) : includes,
    search: searchTerm,
    tagFilter,
    filter,
    favoritesFirst: favoritesFirst || undefined,
    path,
  }

  if (orderBy) {
    requestData.orderBy = orderBy
  }

  if (sortOrder === SORT_ORDER.ASC) {
    requestData.desc = 0
  }
  return requestData
}

const ALLOWED_FIELDS = [
  'recordDate',
  'entryDate',
  'name',
  'notes',
  'receiptNumber',
  'tags',
  'metaData',
  'xmlData',
  'supplier',
  'customer',
  'isManuallyPaid',
  'documentInvoiceDetails',
  // FLAG:useDocuAi — top-level flag the central document-review save sends alongside
  // documentInvoiceDetails to request expense creation. Without it in the whitelist the
  // "Ausgabe erstellen" checkbox never reaches the server (F1).
  'createExpenseFromReview',
  // FLAG:useDocuAi — explicit main-review Save marker. Prevents incidental metadata saves that
  // carry documentInvoiceDetails from creating or updating supplier/customer master data.
  'saveReviewPayee',
  // FLAG:useDocuAi — re-review marker: this save re-opens an already reviewed invoice, so
  // the server re-derives the draft expense instead of treating it as an incidental file update.
  'reReviewSave',
  // FLAG:useDocuAi — explicit payments captured inside the central review travel in the same
  // request; the server validates and applies them only after it created/synced the target receipt.
  'bookingAssignments',
  'linkedTodoFiles',
]

// FLAG:useDocuAi — every field the central review save (receipt-edit.tsx
// buildDocumentInvoiceDetails) writes MUST be listed here, or filterAllowedFields silently
// drops it before the PUT — the reviewer's edits (positions incl. tax account assignments,
// VAT entry, payment method and review-lifecycle fields) are lost (F1). This set must stay
// in sync with editFile.js validateBody (server-accepted) and the client builder — enforced
// by api/test/unit/file/reviewSaveWhitelistParity.test.js. reviewedAt is deliberately
// omitted: it is server-owned (set on completion), never sent by the client.
export const ALLOWED_DOCUMENT_INVOICE_DETAILS_FIELDS = [
  'invoiceNumber',
  'accountHolder',
  'iban',
  'bic',
  'bankInstitute',
  // useDocuAi — the recognized payee IBANs are reviewer-owned: the review component lets the
  // reviewer correct an entry and delete an account the scan read off a footer/imprint, and
  // the curated list is saved back onto the same column it was ingested into.
  'payeeIbans',
  // useDocuAi — the ingested per-account bank block that rides along with payeeIbans
  // (contract 1.7.0). Not typed by the reviewer, but it must stay index-aligned with the
  // curated IBAN list: a deleted card drops its account row too, so the save has to carry
  // the list or the bank line would end up next to the wrong number after a reload.
  'payeeAccounts',
  // useDocuAi — same for the buyer's debit account: the masked account that direct debit
  // debits is reviewer-editable, so the corrected value has to survive this whitelist.
  'debitIbans',
  'totalAmount',
  'date',
  'paymentDueDate',
  'payedAt',
  // monetary + tax detail the reviewer can correct
  'priceNet',
  'vatAmount',
  // useDocuAi — whole-invoice document-level discount (positionsTotal − documentAllowance = priceNet)
  'positionsTotal',
  'documentAllowance',
  'currency',
  'priceKind',
  'vat',
  // line items incl. tax account assignments — the central reviewer edit
  'positions',
  'invoiceFacts',
  'payKindId',
  // review lifecycle (status/scope/provenance/source) written on save
  'reviewStatus',
  'reviewScope',
  'reviewProvenance',
  'reviewSource',
  // invoice-owned supplier/customer identity + address snapshot
  'reviewPayee',
  // useDocuAi — the reviewer's explicit "expense or revenue?" choice for a document that carries no
  // upload intent. Dropping it here would be the worst kind of silent failure: the save would
  // succeed, the checkbox would come back unticked after the reload, and the receipt would be
  // booked into the other book.
  'bookingTarget',
  'requiresAccountingReview',
  'positionsReviewed',
  // the reviewer-editable payment fields must survive the updateFileInfo whitelist,
  // or the save silently drops them before the PUT.
  'openAmount',
  'paymentStatus',
  'paymentMethodCode',
  'paymentCardNumber',
  'paymentCardScheme',
]

const filterAllowedFields = (file) => {
  const filteredFile = {}

  const isFieldAllowed = (fieldPath) => {
    return ALLOWED_FIELDS.some(allowed => {
      return fieldPath === allowed || fieldPath.startsWith(allowed + '.')
    })
  }

  const copyAllowedFields = (source, target, prefix = '') => {
    Object.entries(source).forEach(([key, value]) => {
      const fieldPath = prefix ? `${prefix}.${key}` : key
      if (isFieldAllowed(fieldPath)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          target[key] = {}
          copyAllowedFields(value, target[key], fieldPath)
          if (Object.keys(target[key]).length === 0) {
            delete target[key]
          }
        } else {
          target[key] = value
        }
      }
    })
  }

  copyAllowedFields(file, filteredFile)
  if (filteredFile.documentInvoiceDetails) {
    filteredFile.documentInvoiceDetails = pick(filteredFile.documentInvoiceDetails, ALLOWED_DOCUMENT_INVOICE_DETAILS_FIELDS)
  }
  return filteredFile
}

export const FileService = {
  fetchFolders: () => request(`${documentConfig.routes.folders}`, {
    method: 'GET',
    auth: true,
    isConcurrent: true,
    headers: getCollaborationRequestHeader(),
  }),

  fetchFileList: (pagination, options) => {
    const requestData = dataToSend(pagination, options)
    return request(`${resourceHost}file`, {
      auth: true,
      isConcurrent: true,
      data: requestData,
      headers: getCollaborationRequestHeader(),
    }).then((response) => {
      const { body } = response
      return body
    })
  },

  fetchFileCount: (pagination, options) => {
    const requestData = dataToSend(pagination, options)
    return request(`${resourceHost}file/count`, {
      auth: true,
      isConcurrent: true,
      data: requestData,
      headers: getCollaborationRequestHeader(),
    }).then((response) => {
      const { body } = response
      return body
    })
  },

  fetchFileBlobByUrl: (url, tenantCollaborationId) => {
    return new Promise((resolve, reject) => {
      fetch(url, {
        method: 'GET',
        headers: {
          authorization: `Bearer ${user.token}`,
          ...getCollaborationRequestHeader(tenantCollaborationId),
        },
      })
        .then(response => {
          resolve(response.blob())
        })
        .catch(e => reject(e))
    })
  },

  fetchPreviewUrl: (url) => {
    return new Promise((resolve, reject) => {
      request(url, {
        auth: true,
        isConcurrent: true,
        method: 'GET',
        headers: getCollaborationRequestHeader(),
      })
        .then(({ body: { data: { url, shallRebuild } } }) => {
          resolve({ url, shallRebuild })
        })
        .catch(e => reject(e))
    })
  },

  fetchFileDataByUrl: (url) => {
    return new Promise((resolve, reject) => {
      fetch(url, {
        method: 'GET',
        headers: {
          authorization: `Bearer ${user.token}`,
          ...getCollaborationRequestHeader(),
        },
      })
        .then(response => {
          return response.blob()
        })
        .then(blob => {
          const reader = new FileReader()

          reader.addEventListener('load', () => {
            resolve(reader.result)
          }, false)
          reader.readAsDataURL(blob)
        })
        .catch(e => reject(e))
    })
  },

  fetchDocumentInfo: (id) => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}documents/${id}/info`, {
        auth: true,
        isConcurrent: true,
        method: 'GET',
        headers: getCollaborationRequestHeader(),
      })
        .then(result => resolve({
          data: result.body.data,
          folderAccess: result.body.folderAccess,
          receiptReviewAccess: result.body.receiptReviewAccess,
        }))
        .catch(error => reject(error))
    })
  },

  fetchDrawerPage: (documentId) => {
    const { currentFolder, options, sortModel: sortModelState, pagination: paginAtion, selectedItems } = getAppState().document.list
    const { quickFilter, filterItems, filterModel = {} } = getList(DOCUMENTS_LIST_TYPE)
    const { searchTerm, tagFilter = {} } = options
    const { tags = [], date = [] } = tagFilter
    const { sortOrder, orderBy } = paginAtion

    const sortModel = sortOrder && orderBy
      ? [{ sort: sortOrder, colId: orderBy }]
      : sortModelState || [{ sort: SORT_ORDER.DESC, colId: 'createdAt' }]

    const folderId = (!currentFolder || currentFolder.isRoot) ? undefined : currentFolder.id

    const dateFilter = buildDateFilter(date)

    const filterModelBase = { ...filterModel, ...quickFilter, ...dateFilter }
    const tagIds = tags.length ? tags.map(({ id }) => id) : undefined
    const data = {
      documentId,
      sortModel,
      filterModel: filterModelBase,
      filterItems,
      searchTerm,
      folderId,
      tagIds,
      selected: selectedItems.map(({ id }) => id),
      includes: options.includes,
    }

    return new Promise((resolve, reject) => {
      return request(`${resourceHost}documents/drawerPage`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        headers: getCollaborationRequestHeader(),
        data,
      })
        .then(result => resolve(result.body.data))
        .catch(error => reject(error))
    })
  },

  fetchFileInfo: (fileId) => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}file/${fileId}/meta`, {
        auth: true,
        isConcurrent: true,
        method: 'GET',
        headers: getCollaborationRequestHeader(),
      })
        .then(result => resolve(result))
        .catch(error => reject(error))
    })
  },

  // FLAG:useDocuAi — Layer 3 reconnect resync: fetch the authoritative analysis-state metaData for a
  // batch of files (rows the client still believes are analyzing after a ws reconnect / tab refocus).
  // Returns { data: [{ id, metaData: { analysisInProgress, aiProcessed, ocr, analysisStartedAt } }], meta: {} }.
  resyncAnalysisState: (ids) => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}document/resync-analysis-state`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data: { ids },
        headers: getCollaborationRequestHeader(),
      })
        .then(result => resolve(result))
        .catch(error => reject(error))
    })
  },

  // FLAG:useDocuAi — confirm that a batch of FILE:ANALYSED terminal events was APPLIED, clearing
  // their outstanding uiEventDeliveries rows so the worker resend job stops re-broadcasting them.
  // Called only through the shared ui-event-ack helper (document/utils/ui-event-ack.ts) — never
  // per host — so every consumer of the event acks the same way. Returns { data: { acked } }.
  ackUiEvents: (eventIds) => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}document/ui-event-ack`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data: { eventIds },
        headers: getCollaborationRequestHeader(),
      })
        .then(result => resolve(result))
        .catch(error => reject(error))
    })
  },

  // FLAG:useDocuAi — read-only preview of what a re-review save would change (which
  // expense is re-derived, which is booked and stays untouched, whether the edited payee still
  // matches the linked master record). Backs the confirmation dialog; changes nothing.
  // createExpense mirrors the "Ausgabe erstellen" checkbox — with it ticked and no expense yet,
  // the save creates one, and the dialog must announce that instead of the opposite.
  // bookingTarget likewise mirrors the „Dieser Beleg ist eine Einnahme" check box: the preview
  // must use the decision CURRENTLY in the form, otherwise it announces the old ledger. Without
  // the parameter (any host that does not ask the question), the server reads the stored state —
  // the URL then looks exactly as before.
  fetchReviewSaveEffects: (fileId, { createExpense = false, bookingTarget = null, directoryRef = null, payeeRole = null } = {}) => {
    const bookingTargetParam = bookingTarget ? `&bookingTarget=${encodeURIComponent(bookingTarget)}` : ''
    // FLAG:useDocuAi — T19: the master record the FORM is linked to. On a first review the stored
    // snapshot has no link yet, so without this the preview cannot ask the party question.
    const linkedParam = directoryRef?.type && directoryRef?.id != null
      ? `&linkedType=${encodeURIComponent(directoryRef.type)}&linkedId=${encodeURIComponent(directoryRef.id)}${payeeRole ? `&payeeRole=${encodeURIComponent(payeeRole)}` : ''}`
      : ''
    return request(`${resourceHost}file/${fileId}/review-effects?createExpense=${createExpense ? 'true' : 'false'}${bookingTargetParam}${linkedParam}`, {
      auth: true,
      isConcurrent: true,
      method: 'GET',
      headers: getCollaborationRequestHeader(),
    })
  },

  // FLAG:useDocuAi — „In Prüfung zurücksetzen": put a reviewed incoming invoice back to the state
  // the DocuAI analysis produced (values re-derived server-side from the stored analysis, review
  // back to "Zu prüfen", a derived DRAFT expense removed). Refused for a booked expense —
  // getResetInvoiceReviewErrorCode turns that into the EXPENSE_BOOKED toast.
  resetInvoiceReview: (fileId) => {
    return request(`${resourceHost}file/${fileId}/review/reset`, {
      auth: true,
      isConcurrent: true,
      method: 'POST',
      headers: getCollaborationRequestHeader(),
    })
  },

  updateFileInfo: (file) => {
    return new Promise((resolve, reject) => {
      const filteredFile = filterAllowedFields(file)
      return request(`${resourceHost}file/${file.id}/meta`, {
        auth: true,
        isConcurrent: true,
        method: 'PUT',
        headers: getCollaborationRequestHeader(),
        data: filteredFile,
      })
        .then(result => resolve(result))
        .catch(error => reject(error))
    })
  },

  deleteFile: (fileId) => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}file/${fileId}`, {
        auth: true,
        isConcurrent: true,
        method: 'DELETE',
      })
        .then(result => resolve(result))
        .catch(error => reject(error))
    })
  },

  deleteFiles: (fileIds) => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}file/delete`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data: { fileIds },
      })
        .then(result => resolve(result))
        .catch(error => reject(error))
    })
  },

  setTags: (fileIds, tagId, remove = false) => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}file/batchAssignTag`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data: { fileIds, tagId, remove },
        headers: getCollaborationRequestHeader(),
      })
        .then(result => resolve(result))
        .catch(error => reject(error))
    })
  },

  addNewTag: (tag) => {
    const newTag = { ...tag, tagType: 'documents' }
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}tags/`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data: newTag,
      })
        .then(result => resolve(result))
        .catch(error => reject(error))
    })
  },

  dmsDownloadZip: (minioIds, filename) => {
    return new Promise((resolve, reject) => {
      const { dmsZipDownloadUrl } = getAppState().tenant.settings

      return withDmsFrontendToken(({ dmsApiClient, dmsToken }) => axios.post(dmsZipDownloadUrl, { minioIds, filename }, {
        headers: {
          Authorization: `Bearer ${dmsToken}`,
          'Api-Client': dmsApiClient,
        },
        responseType: 'blob',
      }))
        .then(({ data, headers }) => {
          download(data, `${filename}.zip`, headers['content-type'])

          setTimeout(resolve, 3000)
        })
        .catch((e) => {
          const buffer = Buffer.from(e).toString()
          reject(JSON.parse(buffer))
        })
    })
  },

  downloadZip: (fileIds, folderName = '') => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}file/downloadZip`, {
        responseType: 'arraybuffer',
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data: { fileIds },
        headers: getCollaborationRequestHeader(),
        timeout: 120000,
      })
        .then(({ response }) => {
          download(response.data, `Download${folderName ? '-' + folderName[folderName.length - 1] : ''}-${moment().format('DD.MM.YYYY - HH:mm')}.zip`, response.headers['content-type'])

          setTimeout(resolve, 3000)
        })
        .catch(({ body }) => {
          const buffer = Buffer.from(body).toString()
          reject(JSON.parse(buffer))
        })
    })
  },

  getFileIds: (folderPath) => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}file/getFileIds`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data: { folderPath },
        headers: getCollaborationRequestHeader(),
        timeout: 120000,
      })
        .then(({ body: { data } }) => {
          resolve(data)
        })
        .catch((e) => {
          reject(e?.response?.data ?? {})
        })
    })
  },

  batchSetFavorite: (ids, isFavorite, { filter, searchTerm }) => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}file/batchSetFavorite`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data: { ids, isFavorite, filter, searchTerm },
      })
        .then(resolve)
        .catch(reject)
    })
  },
  batchSetFolderFavorite: (ids, isFavorite) => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}file/setFolderFavorite`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data: { ids, isFavorite },
      })
        .then(resolve)
        .catch(reject)
    })
  },

  setShareHUBState: (id, isShared) => {
    return new Promise((resolve, reject) => {
      return request(`${resourceHost}file/${id}/set-shared`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data: { isShared },
      })
        .then(({ body }) => {
          resolve(body)
        })
        .catch((e) => {
          reject(e)
        })
    })
  },

  mkDir: (path, activeFilter, permissions = []) => {
    return new Promise((resolve, reject) => {
      return request(`${config.resourceHost}folder/create`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        headers: getCollaborationRequestHeader(),
        data: { folderPath: `${path}/`, activeFilter, permissions },
      })
        .then(({ body: { folders } }) => {
          updateFolders(folders)
          resolve(folders)
        })
        .catch((e) => {
          reject(e)
        })
    })
  },

  renameDir: (id, path, activeFilter) => {
    return new Promise((resolve, reject) => {
      return request(`${config.resourceHost}folder/${id}/rename`, {
        auth: true,
        isConcurrent: true,
        method: 'PATCH',
        headers: getCollaborationRequestHeader(),
        data: { newPath: `${path}/`, activeFilter: activeFilter || 'all' },
      })
        .then(({ body: { folders } }) => {
          updateFolders(folders)
          resolve()
        })
        .catch((e) => {
          const { body: { meta } } = e
          let message = lang.documents.folder.renameFailed
          if (meta.name?.[0].code === errorCodes.TOO_LARGE) {
            message = lang.documents.folder.nameTooLong
          } else if (meta.newPath?.[0].code === errorCodes.REQUIRED) {
            message = lang.documents.folder.nameTooShortRename
          }
          toast.showToast({ type: 'error', message })
          reject(e)
        })
    })
  },

  rmDir: (id) => {
    return new Promise((resolve, reject) => {
      return request(`${config.resourceHost}folder/${id}`, {
        auth: true,
        isConcurrent: true,
        method: 'DELETE',
        headers: getCollaborationRequestHeader(),
      })
        .then(({ body: { folders } }) => {
          updateFolders(folders)
          resolve()
        })
        .catch((e) => {
          reject(e)
        })
    })
  },

  mvDir: (id, folderId) => {
    return new Promise((resolve, reject) => {
      return request(`${config.resourceHost}folder/${id}/move`, {
        auth: true,
        isConcurrent: true,
        method: 'PATCH',
        headers: getCollaborationRequestHeader(),
        data: { folderId },
      })
        .then(({ body: { folders } }) => {
          updateFolders(folders)
          resolve()
        })
        .catch((e) => {
          reject(e)
        })
    })
  },

  markAsSeen: (path, fileIds = []) => {
    return new Promise((resolve, reject) => {
      return request(`${config.resourceHost}file/markAsSeen`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        headers: getCollaborationRequestHeader(),
        data: { fileIds, path },
      })
        .then(() => {
          FileService.updateCollaboratorUnseenCount()
          dispatch(fetchListAction({ clear: true }))
          dispatch(fetchFolders())

          resolve()
        })
        .catch((e) => {
          reject(e)
        })
    })
  },

  updateCollaboratorUnseenCount: async(useTenantId = false) => {
    try {
      const { data } = await TenantCollaboratorsService.getCollaborators()
      data.forEach((collaborator) => {
        if (collaborator.hasDocumentAccess) {
          const { tenantCollaborations, hasUnseenFiles } = collaborator
          const id = useTenantId ? collaborator.id : tenantCollaborations.id

          dispatch(setCollaboratorsData(id, hasUnseenFiles))
        }
      })
    } catch (error) { }
  },

  moveFiles: (fileIds, folderId) => {
    return new Promise((resolve, reject) => {
      return request(`${config.resourceHost}file/move`, {
        auth: true,
        isConcurrent: true,
        method: 'PATCH',
        headers: getCollaborationRequestHeader(),
        data: { fileIds, folderId },
      })
        .then((res) => {
          const { body: { meta } } = res
          if (meta?.skipped?.length) {
            const count = fileIds.length > 1 && fileIds.length === meta.skipped.length ? -1 : meta.skipped.length
            toast.showToast({ type: 'error', message: lang.documents.errors.moveFilesSkipped(count) })
          }
          resolve(res)
        })
        .catch(reject)
    })
  },

  excludeFromDatev: (fileId, excludeFromDatev) => {
    return request(`${resourceHost}file/${fileId}/excludeFromDatev`, {
      auth: true,
      isConcurrent: true,
      method: 'PATCH',
      headers: getCollaborationRequestHeader(),
      data: { excludeFromDatev },
    })
  },

  getXmlValidationReport: async(fileId) => {
    try {
      return request(`${config.resourceHost}file/${fileId}/validation/report`, {
        auth: true,
        isConcurrent: true,
      })
    } catch (error) {
      console.error(error)
      throw error
    }
  },

  fetchPendingUploads: async() => {
    return new Promise((resolve, reject) => {
      return axios.get(`${config.resourceHost}dms-upload/pending`, {
        headers: {
          ...getCollaborationRequestHeader(),
          Authorization: `Bearer ${user.token}`,
        },
      })
        .then(({ data }) => {
          resolve(data)
        })
        .catch((e) => {
          reject(e)
        })
    })
  },

  checkDuplicate: (fileNames, folderId) => {
    return new Promise((resolve, reject) => {
      return request(`${config.resourceHost}file/duplicate`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        headers: getCollaborationRequestHeader(),
        data: { fileNames, folderId },
      })
        .then((res) => {
          resolve(res)
        })
        .catch(reject)
    })
  },

  startSingleUpload: async({ file, checksum, fileName: name, fileId = '', analyze = false, uploadUrl, uploadToken, dmsToken = user.token, dmsApiClient = '', source }) => {
    const formData = new FormData()
    formData.append('file', file, { filename: name })
    formData.append('fileId', fileId)
    formData.append('extractKeyValue', analyze)

    const headers = {
      ...getCollaborationRequestHeader(),
      'content-type': 'multipart/form-data',
      Authorization: `Bearer ${user.token}`,
      'Api-Client': dmsApiClient,
    }
    if (uploadToken) {
      headers['upload-token'] = uploadToken
      headers.Authorization = `Bearer ${dmsToken}`
    }

    const postFile = () => axios.post(
      uploadUrl,
      formData,
      {
        headers,
        ...timeoutFor(source, UPLOAD_TIMEOUT_MS),
        onUploadProgress: (progressEvent) => {
          const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100)
          const pending = getAppState().document.upload.pending.find((item) => item.uploadToken === uploadToken)
          if (pending && (!pending.progress || (progress - pending.progress) >= 5 || progress === 100)) {
            dispatch(updatePending({ uploadToken, progress }))
          }
        },
      })

    dispatch(updatePending({ uploadToken, running: true, pausing: false }))

    // FLAG:useDocuAi — receipt uploads run the hardened path: bounded retry of the transfer, and
    // `finished` deferred until complete-upload has actually resolved. The latter is what keeps the
    // concurrency slot occupied for the whole chain (RC2) — previously the slot was freed at
    // `finished:true` while complete-upload, by far the most expensive call, was still running, so
    // a large batch fired unbounded concurrent completes.
    //
    // Every legacy source keeps the original body below VERBATIM.
    if (isReceiptUploadSource(source)) {
      return FileService.runReceiptSingleUpload({ postFile, checksum, name, uploadToken, analyze, source })
    }

    // FLAG:useDocuAi — Documents uploads run the same bounded retry, but keep the LEGACY ordering
    // (finished before complete-upload) because they are still accounted by runLegacyUploadBatch.
    // See runDocumentSingleUpload for why the two chains must not be merged. Flag-OFF this is false
    // and the untouched legacy body below runs, exactly as before.
    if (isHardenedUploadSource(source)) {
      return FileService.runDocumentSingleUpload({ postFile, checksum, name, uploadToken, analyze, source })
    }

    axios.post(
      uploadUrl,
      formData,
      {
        headers,
        onUploadProgress: (progressEvent) => {
          const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100)
          const pending = getAppState().document.upload.pending.find((item) => item.uploadToken === uploadToken)
          if (pending && (!pending.progress || (progress - pending.progress) >= 5 || progress === 100)) {
            dispatch(updatePending({ uploadToken, progress }))
          }
        },
      })
      .then(async({ data }) => {
        dispatch(updatePending({ uploadToken, maxPartNumber: 1, finished: true, running: false, error: null }))
        if (data?.result?.minioId) {
          const minioId = data.result.minioId
          await completeUpload({ checksum, name, uploadToken, analyze, minioId }).catch((e) => onUploadError(e, { uploadToken }))
        }
      })
      .catch((res) => {
        if (res.response?.data?.minioId) {
          dispatch(updatePending({ uploadToken, maxPartNumber: 1, finished: true, running: false, error: null }))
          return completeUpload({ checksum, name, uploadToken, analyze, minioId: res.response.data.minioId }).catch((e) => onUploadError(e, { uploadToken }))
        } else {
          onUploadError(res, { uploadToken })
        }
      })
  },

  // FLAG:useDocuAi — wires the (unit-tested) receipt upload chain to the real dispatch/axios/error
  // handling. The ordering itself lives in runReceiptSingleUpload so it can be tested without this
  // module's dependency graph.
  //
  // BOTH steps are retried. complete-upload only became safe to repeat once the route was made
  // idempotent (api/src/routes/dms-upload/complete-upload.js): it nulls the uploadToken on success,
  // so a retry after a response lost in transit could not find its own row and used to crash into a
  // silent 500. It now recognises the already-completed file by (tenantId, minioId) and returns it
  // without re-running any side effects. Retrying is therefore either a real completion or a
  // no-op — never a duplicate analysis and never a false failure.
  runReceiptSingleUpload: async({ postFile, checksum, name, uploadToken, analyze, source }) => {
    const reportAttempt = ({ attempt, maxAttempts }) => {
      dispatch(updatePending({ uploadToken, attempt: attempt + 1, maxAttempts }))
    }

    return runReceiptSingleUploadChain({
      postFile: () => withUploadRetry(postFile, { onAttemptFailed: reportAttempt }),
      completeUpload: ({ minioId }) => withUploadRetry(
        () => completeUpload({ checksum, name, uploadToken, analyze, minioId, source }),
        { onAttemptFailed: reportAttempt },
      ),
      patchPending: (patch) => dispatch(updatePending({ uploadToken, ...patch })),
      // Enrich BEFORE onUploadError sees it (same as runDocumentSingleUpload below): a failure
      // with no http response (connection reset, our own timeout) otherwise reaches the row as
      // the bare generic message — this receipt mass-upload path is what the helper was built for.
      reportError: (error) => onUploadError(
        enrichUploadError(error, lang.documents.upload.errors.uploadFailed),
        { uploadToken },
      ),
      uploadFailedMessage: lang.documents.upload.errors.uploadFailed,
    })
  },

  // FLAG:useDocuAi — wires the (unit-tested) Documents upload chain to the real axios/dispatch/error
  // handling. Same retry policy as the receipt path; the ORDERING stays legacy — see
  // runDocumentSingleUpload.
  //
  // Retrying complete-upload is safe here for the same reason it is safe there: the route recognises
  // an already-completed (tenantId, minioId, checksum) and returns that file without repeating any
  // side effect, so a repeat is either a real completion or a no-op — never a duplicate analysis.
  runDocumentSingleUpload: async({ postFile, checksum, name, uploadToken, analyze, source }) => {
    const reportAttempt = ({ attempt, maxAttempts }) => {
      dispatch(updatePending({ uploadToken, attempt: attempt + 1, maxAttempts }))
    }

    return runDocumentSingleUploadChain({
      postFile: () => withUploadRetry(postFile, { onAttemptFailed: reportAttempt }),
      completeUpload: ({ minioId }) => withUploadRetry(
        () => completeUpload({ checksum, name, uploadToken, analyze, minioId, source }),
        { onAttemptFailed: reportAttempt },
      ),
      patchPending: (patch) => dispatch(updatePending({ uploadToken, ...patch })),
      // Enrich BEFORE onUploadError sees it: a failure with no http response (connection reset, our
      // own timeout) otherwise reaches the row as the bare generic message, which is why a failed
      // mass upload could not be diagnosed after the fact.
      reportError: (error) => onUploadError(
        enrichUploadError(error, lang.documents.upload.errors.uploadFailed),
        { uploadToken },
      ),
    })
  },

  startMultipartUpload: async({ file, uploadToken, checksum, totalChunks, maxPartNumber, status, fileName: name, analyze = false, uploadUrl, dmsToken = user.token, dmsApiClient = '' }) => {
    let startChunkIndex = 0

    if (maxPartNumber && maxPartNumber > 0) {
      if (status === 'completed') {
        return
      } else if (status === 'failed') {
        return
      } else if (status === 'in-progress') {
        startChunkIndex = maxPartNumber
      }
    }

    dispatch(updatePending({ uploadToken, running: true, pausing: false, error: null }))

    for (let chunkIndex = startChunkIndex; chunkIndex < totalChunks; chunkIndex++) {
      const uploadState = getAppState().document.upload

      const start = chunkIndex * CHUNK_SIZE
      const end = Math.min(file.size, start + CHUNK_SIZE)
      const chunk = file.slice(start, end)

      const formData = new FormData()
      formData.append('partData', chunk)
      formData.append('partNumber', chunkIndex + 1)

      const { pausing } = uploadState.pending.find((item) => item.checksum === checksum)
      if (pausing) {
        dispatch(updatePending({ uploadToken, running: false, pausing: false }))
        return
      }

      try {
        await axios.post(uploadUrl, formData, {
          headers: {
            ...getCollaborationRequestHeader(),
            'upload-token': uploadToken,
            'Content-Range': `bytes ${start}-${end - 1}/${file.size}`,
            Authorization: `Bearer ${dmsToken}`,
            'Api-Client': dmsApiClient,
          },
          onUploadProgress: (progressEvent) => {
            const progressPerPart = 100 / totalChunks
            const progress = Math.floor(chunkIndex * progressPerPart + (progressEvent.loaded / progressEvent.total) * progressPerPart)
            const pending = getAppState().document.upload.pending.find((item) => item.uploadToken === uploadToken)
            if (!pending.progress || (progress - pending.progress) >= 5 || progress === 100) {
              dispatch(updatePending({ uploadToken, maxPartNumber: chunkIndex + 1, progress }))
            }
          },
        })
          .then(() => {
            dispatch(updatePending({ uploadToken, blocked: false }))
          })
      } catch (error) {
        onUploadError(error, { uploadToken })
        return
      }
    }

    await completeUpload({ checksum, name, uploadToken, analyze })
      .then(() => {
        dispatch(updatePending({ uploadToken, finished: true, running: false, error: null }))
      })
      .catch((e) => onUploadError(e, { uploadToken }))
  },

  prepareUpload: async(data, params, analyze = false, totalChunks) => {
    const {
      file,
      uuid,
      name,
      size,
      checksum,
      source,
    } = data
    const {
      metaData,
      recordDate,
      tags,
      notes,
      folderId,
      customerId,
      supplierId,
      // Duplicate-upload detection: set on the RE-call after the user answered the
      // "Dokument bereits vorhanden" modal (see the duplicateCandidate branch below).
      forceDuplicate,
      duplicateResolution,
    } = params

    const payload = {
      name: customEncodeUri(name),
      checksum,
      size,
      metaData,
      recordDate,
      tags,
      mimetype: file.type || 'application/octet-stream',
      notes,
      totalChunks,
      chunkSize: CHUNK_SIZE,
      analyze,
      folderId,
      customerId,
      supplierId,
      // Duplicate-upload detection is a per-request CLIENT opt-in: only this uploader understands
      // the duplicateCandidate / reuse response shapes, so the server only runs the check when we
      // ask for it. Every other client (mailbox, mobile, desktop) keeps the plain token response.
      duplicateDetection: true,
      forceDuplicate,
      duplicateResolution,
    }

    const postPrepare = () => axios.post(`${config.resourceHost}dms-upload/prepare-upload`, payload, {
      headers: {
        ...getCollaborationRequestHeader(),
        Authorization: `Bearer ${user.token}`,
      },
      ...timeoutFor(source, PREPARE_TIMEOUT_MS),
    })

    // FLAG:useDocuAi — hardened uploads (receipts always, Documents with the flag on) retry a
    // transient prepare failure (no response / 429 / 5xx gateway). Safe to repeat: prepare-upload
    // creates its File row inside a transaction that rolls back on failure, so a retry cannot leave
    // a duplicate. Legacy sources call postPrepare once, exactly as before.
    return (isHardenedUploadSource(source) ? withUploadRetry(postPrepare) : postPrepare())
      .then(async({ data: { data: responseData, meta } }) => {
        // Duplicate-upload detection: the server recognised this exact document (same checksum)
        // and returned a candidate INSTEAD of an upload token. Ask the user, then re-run the
        // prepare with their choice. Destructure defensively — `data` is null in this shape.
        if (meta?.duplicateCandidate) {
          const useExisting = await askDuplicateUploadResolution(meta.duplicateCandidate.name || name)

          return FileService.prepareUpload(
            data,
            {
              ...params,
              ...(useExisting ? { duplicateResolution: 'reuse' } : { forceDuplicate: true }),
            },
            analyze,
            totalChunks,
          )
        }

        const { uploadToken, status, id, fileId, folderId, maxPartNumber } = responseData || {}
        const { dmsToken, dmsApiClient, uploadUrl } = meta || {}

        // "Vorhandenes Dokument verwenden": nothing to upload — the server re-analysed the document
        // we already have. Settle the queue entry as finished so the batch promise completes
        // (classifyBatchUploads treats a finished pending row, matched by fileName, as terminal).
        if (!uploadToken && status === 'completed') {
          dispatch(addPending({
            file,
            fileId,
            folderId,
            checksum,
            fileName: name,
            analyze,
            totalChunks,
            maxPartNumber: 0,
            uploadToken: `duplicate:${uuid}`,
            status,
            id,
            running: false,
            finished: true,
            error: null,
            // FLAG:useDocuAi — batch identity, so classifyBatchUploads joins this row to its
            // batch entry by uuid rather than by file name.
            uuid,
            source,
          }))
          dispatch(removeChecksumCalculation(uuid))
          return
        }

        const pending = {
          file,
          fileId,
          folderId,
          checksum,
          fileName: name,
          analyze,
          totalChunks,
          maxPartNumber,
          uploadToken,
          status,
          id,
          running: true,
          dmsToken,
          dmsApiClient,
          uploadUrl,
          // FLAG:useDocuAi — batch identity (classifyBatchUploads joins on this) and the source
          // that selects the hardened upload path in startSingleUpload.
          uuid,
          source,
        }

        dispatch(addPending(pending))
        dispatch(removeChecksumCalculation(uuid))

        if (file.size > SINGLE_UPLOAD_MAX_SIZE) {
          FileService.startMultipartUpload(pending)
        } else {
          FileService.startSingleUpload(pending)
        }
      })
      // Hardened sources get the enriched error (status/code detail for the row); legacy
      // sources keep the untouched baseline error — the enrichment is flag-scoped by source.
      .catch((res) => onUploadError(
        isHardenedUploadSource(source) ? enrichUploadError(res, lang.documents.upload.errors.uploadFailed) : res,
        { uuid },
      ))
  },

  // FLAG:useDocuAi — manual retry for a row whose bytes already reached the DMS and only
  // complete-upload failed. Re-running startSingleUpload (what the flyout retry did for every
  // failure) would push the file to the DMS a SECOND time and create another version; the only
  // step that actually needs repeating is the completion. Safe to press repeatedly — the route is
  // idempotent for an already-completed (tenantId, minioId).
  retryCompleteUpload: async({ checksum, fileName: name, uploadToken, analyze, minioId, source }) => {
    dispatch(updatePending({ uploadToken, completing: true, running: true, error: null }))
    try {
      await completeUpload({ checksum, name, uploadToken, analyze, minioId, source })
      dispatch(updatePending({ uploadToken, finished: true, completing: false, running: false, error: null }))
    } catch (error) {
      dispatch(updatePending({ uploadToken, completing: false, running: false }))
      onUploadError(error, { uploadToken })
    }
  },

  cancelUpload: async(payload) => {
    return axios.post(`${config.resourceHost}dms-upload/cancel-upload`, payload, {
      headers: {
        ...getCollaborationRequestHeader(),
        Authorization: `Bearer ${user.token}`,
      },
    })
  },

  getDownloadLink: async(payload) => {
    return axios.post(`${config.resourceHost}dms-specific/download-token`, payload, {
      headers: {
        ...getCollaborationRequestHeader(),
        Authorization: `Bearer ${user.token}`,
      },
    })
  },

  getAllowedMimetypes: async() => {
    return axios.get(`${config.resourceHost}dms-upload/mimetypes`, {
      headers: {
        ...getCollaborationRequestHeader(),
        Authorization: `Bearer ${user.token}`,
      },
    })
  },

  // FLAG:useDocuAi — same DMS fetch as `streamDmsFile`, but resolves a blob object URL
  // instead of a base64 `data:` URL. For MEDIA that difference is load-bearing:
  //   · base64 inflates the payload ~33% on top of a full in-memory copy of the file, and
  //   · a `data:` URL gives the browser no byte ranges, so <video> cannot seek until the
  //     whole thing is decoded — scrubbing a movie is exactly what the player is for.
  // A blob URL is seekable because the browser already holds the bytes. It is the same
  // mechanism the DMS admindashboard's viewer uses (URL.createObjectURL(fileBlob)).
  // CALLER OWNS THE URL: revoke it with URL.revokeObjectURL when done, or it leaks the
  // whole file for the lifetime of the document.
  streamDmsFileAsObjectUrl: (minioId, mimeType) => {
    const { dmsFileUrl } = getAppState().tenant.settings

    return withDmsFrontendToken(({ dmsApiClient, dmsToken }) => axios.post(dmsFileUrl, { id: minioId }, {
      headers: {
        Authorization: `Bearer ${dmsToken}`,
        'Api-Client': dmsApiClient,
      },
      responseType: 'blob',
    })).then(({ data }) => URL.createObjectURL(
      // Re-wrap so the element gets an explicit type: without it Safari refuses to play a
      // blob whose MIME the response did not pin down.
      mimeType ? new Blob([data], { type: mimeType }) : data,
    ))
  },

  streamDmsFile: (minioId) => {
    const { dmsFileUrl } = getAppState().tenant.settings

    return new Promise((resolve, reject) => {
      withDmsFrontendToken(({ dmsApiClient, dmsToken }) => axios.post(dmsFileUrl, { id: minioId }, {
        headers: {
          Authorization: `Bearer ${dmsToken}`,
          'Api-Client': dmsApiClient,
        },
        responseType: 'blob',
      }))
        .then(({ data }) => {
          const reader = new FileReader()

          reader.addEventListener('load', () => {
            resolve(reader.result)
          }, false)
          reader.readAsDataURL(data)
        })
        .catch(e => reject(e))
    })
  },

  // FLAG:docuAiEnhancements — "Analyse-Problem melden": report an analysis problem about
  // a document straight to the DMS public API (reusing the frontend's dmsToken).
  // The DMS persists it on the Version and forwards it to DocuAI's issue worklist.
  // `target` selects the DMS engine: omitted/'docuai' = document analysis problem
  // (needs minioId); 'dms' = a search problem (pass searchTerm instead of minioId,
  // the search is served by the DMS searchapi).
  // `target: 'ragchat'` is the receipt-chat path: a KNOWLEDGE-BASE gap, not a document problem.
  // It needs no minioId (though it may carry one when the chat sits next to an open receipt) and
  // sends the submitted exchange instead — the DMS merges question/answer into the issue text with
  // the same builder its own operator chat uses.
  reportProblem: ({
    minioId = undefined,
    description,
    title = undefined,
    target = undefined,
    searchTerm = undefined,
    question = undefined,
    answer = undefined,
  }) => {
    const { dmsReportProblemUrl } = getAppState().tenant.settings
    return withDmsFrontendToken(({ dmsApiClient, dmsToken }) => axios.post(dmsReportProblemUrl, { minioId, description, title, target, searchTerm, question, answer }, {
      headers: {
        Authorization: `Bearer ${dmsToken}`,
        'Api-Client': dmsApiClient,
      },
    }))
  },

  // FLAG:docuAiEnhancements — map a failed report-problem POST to the user-facing hint.
  // The report dialogs stay open on failure (close() only runs on success) and the text
  // is preserved in the ref, so "try again" is honest advice for transient failures.
  getReportProblemErrorMessage: (error) => {
    // No HTTP response at all = network down / server unreachable / timeout.
    if (!error?.response) return lang.documents.drawer.footer.reportProblemNetworkError
    // 429: the DMS anti-spam cap (MELDUNGEN_MAX_OPEN_PER_TENANT) — too many open tickets.
    if (error.response.status === 429) return lang.documents.drawer.footer.reportProblemTooMany
    return lang.documents.drawer.footer.reportProblemError
  },

  // FLAG:docuAiEnhancements — "Meine Meldungen": lists this tenant's submitted issues with
  // their coarse customer state + the polite customer message, straight from the DMS public
  // API (same endpoint as reportProblem; GET = list). The DMS returns ONLY the customer-safe
  // projection (never the internal engine status/source). Per-tenant by the dmsToken.
  listMyIssues: ({ page = 1, limit = 50 } = {}) => {
    const { dmsReportProblemUrl } = getAppState().tenant.settings
    return withDmsFrontendToken(({ dmsApiClient, dmsToken }) => axios.get(dmsReportProblemUrl, {
      params: { page, limit },
      headers: {
        Authorization: `Bearer ${dmsToken}`,
        'Api-Client': dmsApiClient,
      },
    }))
  },
}

export const getCollaboratorDmsFrontendToken = (id) => axios.get(`${config.resourceHost}tenant/collaborators/${id}/dmsToken`, {
  headers: {
    Authorization: `Bearer ${user.token}`,
    ...(id === 'internal' ? {} : getCollaborationRequestHeader(id)),
  },
})

export default FileService
